import { useEffect, useRef } from "react";
import { tinykeys } from "tinykeys";

const handledShortcutEvent = Symbol("handledShortcutEvent");

function alternatePhysicalBinding(bindingKey) {
  const parts = String(bindingKey ?? "").trim().split("+");
  const key = parts.at(-1);
  if (parts.includes("Alt") && /^[A-Za-z]$/.test(key)) {
    return [...parts.slice(0, -1), `Key${key.toUpperCase()}`].join("+");
  }
  if (key === "/") return [...parts.slice(0, -1), "Slash"].join("+");
  if (key === "?") return [...parts.slice(0, -1), "Shift+Slash"].join("+");
  if (key === ";") return [...parts.slice(0, -1), "Semicolon"].join("+");
  return null;
}

function isTypingTarget(target) {
  return target instanceof HTMLElement && (
    target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)
  );
}

function resolveBindingTarget(target) {
  return target && "current" in target ? target.current : target;
}

export function useShortcutBindings(target, bindings) {
  const bindingsRef = useRef(new Map());
  const bindingEntries = bindings.filter((binding) => binding?.binding).flatMap((binding) => {
    const alternate = alternatePhysicalBinding(binding.binding);
    return alternate ? [[binding.binding, binding], [alternate, binding]] : [[binding.binding, binding]];
  });
  const bindingKeysKey = bindingEntries.map(([binding]) => binding).join("\u0000");

  useEffect(() => {
    bindingsRef.current = new Map(bindingEntries);
  }, [bindingEntries]);

  useEffect(() => {
    const bindingTarget = resolveBindingTarget(target);
    const bindingKeys = bindingKeysKey ? bindingKeysKey.split("\u0000") : [];
    if (!bindingTarget || !bindingKeys.length) return undefined;
    const keyBindingMap = Object.fromEntries(bindingKeys.map((bindingKey) => [bindingKey, (event) => {
      if (event[handledShortcutEvent]) return;
      const binding = bindingsRef.current.get(bindingKey);
      if (typeof binding?.onTrigger !== "function" || (!binding.allowInInput && isTypingTarget(event.target))) return;
      event[handledShortcutEvent] = true;
      event.preventDefault();
      binding.onTrigger(event);
    }]));
    return tinykeys(bindingTarget, keyBindingMap);
  }, [bindingKeysKey, target]);
}
